import { IAttributesAllergensClient } from './IAttributesAllergensClient';
import {
  MasterAttribute,
  AttributeListResponse,
  AttributeUsageEntry,
  AttributeConflict,
  AttributeUsageEntityType,
} from './attributesAllergensTypes';
import { masterAttributes } from '../../../../mocks/data/attributesAllergensData';
import { mockRecipesV2 } from '../../../../mocks/data/recipeV2Data';
import { rangePlanningItems } from '../../../../mocks/data/rangePlanningItemsData';
import { menus } from '../../../../mocks/data/menusData';
import { DietaryAttribute, DietaryAttributeStatus } from '../recipeV2/recipeV2Types';

interface UsageEntity {
  entityType: AttributeUsageEntityType;
  entityId: number;
  entityName: string;
  dietaryAttributes: DietaryAttribute[];
}

/** Conflict detection rules: an entity claiming `claimCode` with one of `claimStatuses`
 * should not simultaneously carry any of `conflictsWithCodes` with one of `conflictStatuses`.
 * Kept as a small, explicit rule table (not a generic constraint solver) - see the `lazy:`
 * note below for the upgrade path if more rules are needed later. */
interface ConflictRule {
  claimCode: string;
  claimStatuses: DietaryAttributeStatus[];
  conflictsWithCodes: string[];
  conflictStatuses: DietaryAttributeStatus[];
  description: (entityName: string) => string;
}

const SUITABLE_STATUSES = [DietaryAttributeStatus.Suitable, DietaryAttributeStatus.Certified];

// lazy: fixed rule table covering the common vegan/vegetarian/free-from claims present in
// the demo mock data - add more rows here if new attribute codes need conflict checking.
const conflictRules: ConflictRule[] = [
  {
    claimCode: 'VEGAN',
    claimStatuses: SUITABLE_STATUSES,
    conflictsWithCodes: ['MILK', 'EGGS', 'FISH', 'CRUSTACEANS', 'MOLLUSCS'],
    conflictStatuses: [DietaryAttributeStatus.Contains],
    description: (name) => `${name} is marked Vegan but also contains an animal-derived allergen.`,
  },
  {
    claimCode: 'VEGETARIAN',
    claimStatuses: SUITABLE_STATUSES,
    conflictsWithCodes: ['FISH', 'CRUSTACEANS', 'MOLLUSCS'],
    conflictStatuses: [DietaryAttributeStatus.Contains],
    description: (name) => `${name} is marked Vegetarian but also contains fish or shellfish.`,
  },
  {
    claimCode: 'GLUTEN_FREE',
    claimStatuses: SUITABLE_STATUSES,
    conflictsWithCodes: ['GLUTEN'],
    conflictStatuses: [DietaryAttributeStatus.Contains],
    description: (name) => `${name} is marked Gluten-Free but also contains gluten.`,
  },
  {
    claimCode: 'DAIRY_FREE',
    claimStatuses: SUITABLE_STATUSES,
    conflictsWithCodes: ['MILK'],
    conflictStatuses: [DietaryAttributeStatus.Contains],
    description: (name) => `${name} is marked Dairy-Free but also contains milk.`,
  },
];

/** In-memory mocked client for the Attributes & Allergens governance demo screen. Scans the
 * real RecipeV2, RangePlanningItems and Menus mock data live (rather than maintaining a separate
 * duplicated dataset) so usage counts and conflicts always reflect the actual catalogue. The
 * traceability chain reaches Supplier Item -> Recipe Ingredient -> Recipe -> Menu Item -> Menu
 * by rolling each menu's dietary attributes up from the recipes its dishes reference. */
export class AttributesAllergensClientMocked implements IAttributesAllergensClient {
  private attributes: MasterAttribute[] = [...masterAttributes];

  // NOTE: public methods are arrow-function class fields (not prototype methods) so `this`
  // stays bound to the instance even when a consuming hook destructures them off the client
  // object (e.g. `const { getAttributes } = useAttributesAllergensApi();`) - see
  // menusClientMocked.ts for the full explanation of this gotcha.
  private getAllEntities(): UsageEntity[] {
    const recipeEntities: UsageEntity[] = mockRecipesV2.map((recipe) => ({
      entityType: 'Recipe',
      entityId: recipe.id,
      entityName: recipe.description,
      dietaryAttributes: recipe.dietaryAttributes,
    }));
    const itemEntities: UsageEntity[] = rangePlanningItems.map((item) => ({
      entityType: 'Item',
      entityId: item.id,
      entityName: item.name,
      dietaryAttributes: item.dietaryAttributes,
    }));
    const menuEntities: UsageEntity[] = menus.map((menu) => {
      const recipeIds = new Set<number>();
      menu.courses.forEach((course) => course.dishes.forEach((dish) => recipeIds.add(dish.recipeId)));

      // Conservative rollup: a menu shows an attribute as 'Contains' if any referenced dish
      // does, otherwise it takes whatever status the dishes agree on. This is what lets a
      // single non-suitable dish surface an allergen/dietary flag on the whole menu.
      const byCode = new Map<string, DietaryAttribute>();
      for (const recipeId of Array.from(recipeIds)) {
        const recipe = mockRecipesV2.find((r) => r.id === recipeId);
        if (!recipe) continue;
        for (const da of recipe.dietaryAttributes) {
          const existing = byCode.get(da.code);
          if (!existing || da.status === DietaryAttributeStatus.Contains) {
            byCode.set(da.code, da);
          }
        }
      }

      return {
        entityType: 'Menu',
        entityId: menu.id,
        entityName: menu.name,
        dietaryAttributes: Array.from(byCode.values()),
      };
    });
    return [...recipeEntities, ...itemEntities, ...menuEntities];
  }

  getAttributes = async (search: string, category: string, page: number, pageSize: number): Promise<AttributeListResponse> => {
    const term = search.trim().toLowerCase();
    const filtered = this.attributes.filter((attr) => {
      const matchesSearch = !term || attr.name.toLowerCase().includes(term) || attr.code.toLowerCase().includes(term);
      const matchesCategory = !category || attr.category === category;
      return matchesSearch && matchesCategory;
    });

    const start = page * pageSize;
    return {
      data: filtered.slice(start, start + pageSize),
      totalCount: filtered.length,
    };
  };

  getAttributeDetail = async (code: string): Promise<MasterAttribute> => {
    const attribute = this.attributes.find((a) => a.code === code);
    if (!attribute) throw new Error('Attribute not found');
    return attribute;
  };

  getAttributeUsage = async (code: string): Promise<AttributeUsageEntry[]> => {
    const usage: AttributeUsageEntry[] = [];
    for (const entity of this.getAllEntities()) {
      const match = entity.dietaryAttributes.find((da) => da.code === code);
      if (match) {
        usage.push({
          entityType: entity.entityType,
          entityId: entity.entityId,
          entityName: entity.entityName,
          status: match.status,
          source: match.source,
        });
      }
    }
    return usage;
  };

  getConflicts = async (): Promise<AttributeConflict[]> => {
    const conflicts: AttributeConflict[] = [];
    for (const entity of this.getAllEntities()) {
      const byCode = new Map(entity.dietaryAttributes.map((da) => [da.code, da]));
      for (const rule of conflictRules) {
        const claim = byCode.get(rule.claimCode);
        if (!claim || !rule.claimStatuses.includes(claim.status)) continue;

        for (const conflictCode of rule.conflictsWithCodes) {
          const conflictingAttr = byCode.get(conflictCode);
          if (conflictingAttr && rule.conflictStatuses.includes(conflictingAttr.status)) {
            conflicts.push({
              id: `${entity.entityType}-${entity.entityId}-${rule.claimCode}-${conflictCode}`,
              entityType: entity.entityType,
              entityId: entity.entityId,
              entityName: entity.entityName,
              attributeCode: rule.claimCode,
              conflictingAttributeCode: conflictCode,
              description: rule.description(entity.entityName),
              severity: 'High',
            });
          }
        }
      }
    }
    return conflicts;
  };
}
